import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';
import {
  addTmAssessment,
  removeTmAssessment,
  selectSelectedTmAssessmentId,
  selectTmAssessments,
  setSelectedTmAssessmentId,
  setTmAssessments,
  updateTmAssessment,
} from '../store/tmAssessment/tmAssessmentSlice.ts';
import { createTmAssessment, listTmAssessments, updateTmAssessmentById, deleteTmAssessmentById } from '../../../api.ts';

const TmAssessmentSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const assessments = useAppSelector(selectTmAssessments);
  const selectedAssessmentId = useAppSelector(selectSelectedTmAssessmentId);
  const currentOrg = useAppSelector(selectCurrentOrganization);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const canEdit = currentOrg?.role && currentOrg.role !== 'viewer';
  const selectedAssessment = assessments.find(a => a.id === selectedAssessmentId);

  const loadAssessments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listTmAssessments();
      dispatch(setTmAssessments(data));
    } catch (err: any) {
      console.error('Failed to load assessments', err);
      setError(err?.response?.data?.detail || 'Failed to load assessments');
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadAssessments();
  }, [loadAssessments]);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Assessment name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const created = await createTmAssessment({ name, description });
      dispatch(addTmAssessment(created));
      setDialogOpen(false);
      setName('');
      setDescription('');
    } catch (err: any) {
      console.error('Failed to create assessment', err);
      setError(err?.response?.data?.detail || 'Failed to create assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChange = (rawValue: string | number) => {
    if (rawValue === '') {
      dispatch(setSelectedTmAssessmentId(null));
      return;
    }

    const parsed = Number(rawValue);
    dispatch(setSelectedTmAssessmentId(Number.isNaN(parsed) ? null : parsed));
  };

  const handleOpenEditDialog = () => {
    if (selectedAssessment) {
      setDescription(selectedAssessment.description || '');
      setEditDialogOpen(true);
    }
  };

  const handleUpdateDescription = async () => {
    if (!selectedAssessment?.id) return;

    try {
      setLoading(true);
      setError(null);
      const updated = await updateTmAssessmentById(selectedAssessment.id, { description });
      dispatch(updateTmAssessment(updated));
      setEditDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to update assessment', err);
      setError(err?.response?.data?.detail || 'Failed to update assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAssessment?.id) return;

    try {
      setLoading(true);
      setError(null);
      await deleteTmAssessmentById(selectedAssessment.id);
      dispatch(removeTmAssessment(selectedAssessment.id));
      setDeleteDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to delete assessment', err);
      setError(err?.response?.data?.detail || 'Failed to delete assessment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
      <FormControl sx={{ minWidth: 240 }} size="small">
        <InputLabel id="tm-assessment-select-label">Assessment</InputLabel>
        <Select
          labelId="tm-assessment-select-label"
          value={selectedAssessmentId ?? ''}
          label="Assessment"
          onChange={e => handleSelectChange(e.target.value as string | number)}
        >
          {assessments.map(assessment => (
            <MenuItem key={assessment.id ?? assessment.name} value={assessment.id ?? ''}>
              {assessment.name}
            </MenuItem>
          ))}
          {assessments.length === 0 && (
            <MenuItem disabled value="">
              No assessments found
            </MenuItem>
          )}
        </Select>
        {loading && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Loading assessments...
          </Typography>
        )}
      </FormControl>

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => setDialogOpen(true)}
        disabled={!canEdit}
      >
        New Assessment
      </Button>

      <Button
        variant="outlined"
        startIcon={<EditIcon />}
        onClick={handleOpenEditDialog}
        disabled={!canEdit || !selectedAssessmentId}
      >
        Edit Description
      </Button>

      <Button
        variant="outlined"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={() => setDeleteDialogOpen(true)}
        disabled={!canEdit || !selectedAssessmentId}
      >
        Delete
      </Button>

      {error && (
        <Alert severity="error" sx={{ width: '100%', mt: 1 }}>
          {error}
        </Alert>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Assessment</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoFocus
          />
          <TextField
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={loading || !canEdit}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Assessment Description</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            minRows={4}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateDescription} variant="contained" disabled={loading || !canEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm">
        <DialogTitle>Delete Assessment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the assessment <strong>"{selectedAssessment?.name}"</strong>?
          </Typography>
          <Typography color="error" sx={{ mt: 2 }}>
            This will permanently delete all associated risks, mitigations, actors, and other data. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={loading || !canEdit}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TmAssessmentSelector;
